import React, {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useRef,
  useState,
} from "react";
import Modal from "react-bootstrap/Modal";
import Button from "react-bootstrap/Button";
import SaveToStudyButton from "../components/saveToStudy/SaveToStudyButton";
import "./verseModal.css";

const VerseModalContext = createContext(null);

const BIBLE_GATEWAY_HOSTS = [
  "www.biblegateway.com",
  "biblegateway.com",
];

const VERSE_MODAL_Z_INDEX = 2147483600;
const VERSE_BACKDROP_Z_INDEX = 2147483590;
const UNDERLAY_MODAL_Z_INDEX = 2147483570;
const UNDERLAY_BACKDROP_Z_INDEX = 2147483560;

function isBibleGatewayUrl(value = "") {
  try {
    const url = new URL(value, window.location.origin);

    return (
      BIBLE_GATEWAY_HOSTS.includes(url.hostname) &&
      url.pathname.startsWith("/passage")
    );
  } catch {
    return false;
  }
}

function getReferenceFromUrl(value = "") {
  try {
    const url = new URL(value, window.location.origin);

    return (
      url.searchParams.get("search") ||
      "Scripture Passage"
    );
  } catch {
    return "Scripture Passage";
  }
}

function cleanReference(reference = "") {
  return reference.replace(/\s+/g, " ").trim();
}

function captureInlineStyle(element, property) {
  const value = element.style.getPropertyValue(property);

  if (!value) {
    return null;
  }

  return {
    value,
    priority: element.style.getPropertyPriority(property),
  };
}

function restoreInlineStyle(element, property, savedStyle) {
  if (!element) return;

  if (savedStyle) {
    element.style.setProperty(
      property,
      savedStyle.value,
      savedStyle.priority
    );
  } else {
    element.style.removeProperty(property);
  }
}

export function VerseModalProvider({ children }) {
  const [verseUrl, setVerseUrl] = useState("");
  const [verseReference, setVerseReference] = useState("");
  const [isLoading, setIsLoading] = useState(false);
  const [portalContainer, setPortalContainer] = useState(null);

  const iframeRef = useRef(null);
  const pageScrollPositionRef = useRef(0);

  const originalModalStylesRef = useRef([]);
  const originalBackdropStylesRef = useRef([]);

  const isVerseModalOpen = Boolean(verseUrl);

  /*
   * Create a dedicated portal directly beneath document.documentElement.
   *
   * It has to sit outside <body>, not merely at the top of it. While the
   * modal is open the scroll lock below sets body to `position: fixed` with
   * `top: -<scrollY>px` and `overflow: hidden`, and a fixed body resolves to
   * viewport height rather than content height. That box therefore spans
   * viewport y = -scrollY to (viewportHeight - scrollY).
   *
   * WebKit clips `position: fixed` descendants to a positioned ancestor that
   * has `overflow: hidden` -- Blink does not, which is why this only ever
   * showed up on iOS. Opened after scrolling down N pixels, the modal and its
   * backdrop were both cut off N pixels early, leaving the page visible below
   * the cut. Anchoring the portal to <html>, which stays `position: static`,
   * removes the clipping ancestor entirely.
   */
  useEffect(() => {
    const portal = document.createElement("div");

    portal.id = "verse-modal-portal";
    portal.setAttribute(
      "data-verse-modal-portal",
      "true"
    );

    document.documentElement.appendChild(portal);
    setPortalContainer(portal);

    return () => {
      setPortalContainer(null);

      if (portal.parentNode) {
        portal.parentNode.removeChild(portal);
      }
    };
  }, []);

  const openVerseModal = useCallback(
    (url, reference = "") => {
      if (!url || !isBibleGatewayUrl(url)) return;

      const resolvedReference = cleanReference(
        reference || getReferenceFromUrl(url)
      );

      setVerseReference(resolvedReference);
      setVerseUrl(url);
      setIsLoading(true);
    },
    []
  );

  const closeVerseModal = useCallback(() => {
    setVerseUrl("");
    setVerseReference("");
    setIsLoading(false);
  }, []);

  /*
   * Safety net for the loading pill.
   *
   * It clears on the iframe's load event, but Bible Gateway only fires that
   * once its ads and autoplaying video have finished -- long after the passage
   * is readable -- so the pill used to sit over content the reader could
   * already see. Clear it after a short wait regardless.
   */
  useEffect(() => {
    if (!verseUrl || !isLoading) return undefined;

    const timer = setTimeout(() => setIsLoading(false), 2500);

    return () => clearTimeout(timer);
  }, [verseUrl, isLoading]);

  const openVerseInNewTab = useCallback(() => {
    if (!verseUrl) return;

    const openedWindow = window.open(
      verseUrl,
      "_blank",
      "noopener,noreferrer"
    );

    if (openedWindow) {
      openedWindow.opener = null;
    }
  }, [verseUrl]);

  /*
   * Intercept normal Bible Gateway link clicks throughout the app.
   */
  useEffect(() => {
    function handleVerseClick(event) {
      if (!(event.target instanceof Element)) return;

      const anchor = event.target.closest("a[href]");

      if (!anchor) return;

      /*
       * Never intercept anything inside the Scripture viewer.
       */
      if (
        anchor.closest(".verseModalRoot") ||
        anchor.closest("#verse-modal-portal")
      ) {
        return;
      }

      const href = anchor.href;

      if (!isBibleGatewayUrl(href)) return;

      /*
       * Preserve normal browser behavior for modified clicks.
       */
      if (
        event.metaKey ||
        event.ctrlKey ||
        event.shiftKey ||
        event.altKey ||
        event.button !== 0
      ) {
        return;
      }

      event.preventDefault();
      event.stopPropagation();

      const reference =
        anchor.dataset.verseReference ||
        anchor.getAttribute("aria-label") ||
        anchor.textContent?.trim() ||
        getReferenceFromUrl(href);

      openVerseModal(href, reference);
    }

    document.addEventListener(
      "click",
      handleVerseClick,
      true
    );

    return () => {
      document.removeEventListener(
        "click",
        handleVerseClick,
        true
      );
    };
  }, [openVerseModal]);

  /*
   * Restore all modals and backdrops that were temporarily moved
   * beneath the Scripture modal.
   */
  const restoreUnderlyingLayers = useCallback(() => {
    originalModalStylesRef.current.forEach(
      ({ element, zIndex, position }) => {
        restoreInlineStyle(
          element,
          "z-index",
          zIndex
        );

        restoreInlineStyle(
          element,
          "position",
          position
        );
      }
    );

    originalBackdropStylesRef.current.forEach(
      ({ element, zIndex, position }) => {
        restoreInlineStyle(
          element,
          "z-index",
          zIndex
        );

        restoreInlineStyle(
          element,
          "position",
          position
        );
      }
    );

    originalModalStylesRef.current = [];
    originalBackdropStylesRef.current = [];
  }, []);

  /*
   * Force the Scripture modal above every other open modal.
   */
  const forceVerseModalToFront = useCallback(() => {
    window.requestAnimationFrame(() => {
      const verseModal = document.querySelector(
        ".verseModalRoot"
      );

      const verseBackdrop = document.querySelector(
        ".verseModalBackdrop"
      );

      const otherModals = Array.from(
        document.querySelectorAll(".modal.show")
      ).filter(
        (modal) =>
          !modal.classList.contains("verseModalRoot")
      );

      const otherBackdrops = Array.from(
        document.querySelectorAll(".modal-backdrop")
      ).filter(
        (backdrop) =>
          !backdrop.classList.contains(
            "verseModalBackdrop"
          )
      );

      /*
       * Clear any previous temporary stacking changes before
       * collecting and applying the current modal layers.
       */
      restoreUnderlyingLayers();

      originalModalStylesRef.current = otherModals.map(
        (element) => ({
          element,
          zIndex: captureInlineStyle(
            element,
            "z-index"
          ),
          position: captureInlineStyle(
            element,
            "position"
          ),
        })
      );

      originalBackdropStylesRef.current =
        otherBackdrops.map((element) => ({
          element,
          zIndex: captureInlineStyle(
            element,
            "z-index"
          ),
          position: captureInlineStyle(
            element,
            "position"
          ),
        }));

      otherModals.forEach((modal) => {
        modal.style.setProperty(
          "position",
          "fixed",
          "important"
        );

        modal.style.setProperty(
          "z-index",
          String(UNDERLAY_MODAL_Z_INDEX),
          "important"
        );
      });

      otherBackdrops.forEach((backdrop) => {
        backdrop.style.setProperty(
          "position",
          "fixed",
          "important"
        );

        backdrop.style.setProperty(
          "z-index",
          String(UNDERLAY_BACKDROP_Z_INDEX),
          "important"
        );
      });

      if (verseBackdrop) {
        verseBackdrop.style.setProperty(
          "position",
          "fixed",
          "important"
        );

        verseBackdrop.style.setProperty(
          "z-index",
          String(VERSE_BACKDROP_Z_INDEX),
          "important"
        );
      }

      if (verseModal) {
        verseModal.style.setProperty(
          "position",
          "fixed",
          "important"
        );

        verseModal.style.setProperty(
          "z-index",
          String(VERSE_MODAL_Z_INDEX),
          "important"
        );
      }
    });
  }, [restoreUnderlyingLayers]);

  /*
   * Lock the page behind the Scripture modal.
   *
   * This prevents mouse-wheel, arrow-key, and touch gestures from
   * moving the underlying page when the iframe does not receive them.
   */
  useEffect(() => {
    if (!isVerseModalOpen) return undefined;

    const body = document.body;
    const html = document.documentElement;

    pageScrollPositionRef.current = window.scrollY;

    const originalBodyStyles = {
      position: body.style.position,
      top: body.style.top,
      left: body.style.left,
      right: body.style.right,
      width: body.style.width,
      overflow: body.style.overflow,
      paddingRight: body.style.paddingRight,
    };

    const originalHtmlStyles = {
      overflow: html.style.overflow,
      overscrollBehavior:
        html.style.overscrollBehavior,
    };

    body.classList.add("verseModalIsOpen");

    body.style.position = "fixed";
    body.style.top =
      `-${pageScrollPositionRef.current}px`;
    body.style.left = "0";
    body.style.right = "0";
    body.style.width = "100%";
    body.style.overflow = "hidden";

    html.style.overflow = "hidden";
    html.style.overscrollBehavior = "none";

    return () => {
      body.classList.remove("verseModalIsOpen");

      body.style.position =
        originalBodyStyles.position;
      body.style.top = originalBodyStyles.top;
      body.style.left = originalBodyStyles.left;
      body.style.right = originalBodyStyles.right;
      body.style.width = originalBodyStyles.width;
      body.style.overflow =
        originalBodyStyles.overflow;
      body.style.paddingRight =
        originalBodyStyles.paddingRight;

      html.style.overflow =
        originalHtmlStyles.overflow;
      html.style.overscrollBehavior =
        originalHtmlStyles.overscrollBehavior;

      window.scrollTo(
        0,
        pageScrollPositionRef.current
      );
    };
  }, [isVerseModalOpen]);

  /*
   * Bootstrap adds the modal and backdrop asynchronously, so the
   * stacking logic is repeated briefly while the modal enters.
   */
  useEffect(() => {
    if (!isVerseModalOpen) {
      restoreUnderlyingLayers();
      return undefined;
    }

    const animationFrame =
      window.requestAnimationFrame(
        forceVerseModalToFront
      );

    const shortTimeout = window.setTimeout(
      forceVerseModalToFront,
      50
    );

    const finalTimeout = window.setTimeout(
      forceVerseModalToFront,
      250
    );

    return () => {
      window.cancelAnimationFrame(animationFrame);
      window.clearTimeout(shortTimeout);
      window.clearTimeout(finalTimeout);

      restoreUnderlyingLayers();
    };
  }, [
    isVerseModalOpen,
    forceVerseModalToFront,
    restoreUnderlyingLayers,
  ]);

  return (
    <VerseModalContext.Provider
      value={{
        openVerseModal,
        closeVerseModal,
      }}
    >
      {children}

      {portalContainer && (
        <Modal
          show={isVerseModalOpen}
          onHide={closeVerseModal}
          onEnter={forceVerseModalToFront}
          onEntering={forceVerseModalToFront}
          onEntered={forceVerseModalToFront}
          onExit={restoreUnderlyingLayers}
          centered
          animation
          autoFocus={false}
          enforceFocus={false}
          restoreFocus={false}
          container={portalContainer}
          className="verseModalRoot"
          dialogClassName="verseModalDialog"
          contentClassName="verseModalContent"
          backdropClassName="verseModalBackdrop"
          aria-labelledby="verse-modal-title"
        >
          <Modal.Header closeButton>
            <div className="verseModalHeading">
              <span className="verseModalEyebrow">
                Scripture Passage
              </span>

              <Modal.Title id="verse-modal-title">
                {verseReference ||
                  "Scripture Passage"}
              </Modal.Title>
            </div>
          </Modal.Header>

          <Modal.Body className="verseModalBody">
            {isLoading && (
              <div
                className="verseModalLoading"
                role="status"
                aria-live="polite"
              >
                <span className="verseModalLoadingDot" />

                Loading passage…
              </div>
            )}

            {verseUrl && (
              <iframe
                ref={iframeRef}
                key={verseUrl}
                src={verseUrl}
                title={`${verseReference} on Bible Gateway`}
                className="verseModalFrame"
                tabIndex="0"
                onLoad={() => {
                  setIsLoading(false);
                }}
                onPointerDown={() => {
                  iframeRef.current?.focus();
                }}
                onMouseEnter={() => {
                  iframeRef.current?.focus();
                }}
                allow="clipboard-write"
                referrerPolicy="strict-origin-when-cross-origin"
                scrolling="yes"
              />
            )}
          </Modal.Body>

          <Modal.Footer>
            <span className="verseModalSource">
              Bible Gateway
            </span>

            <div className="verseModalActions">
              {verseReference && verseReference !== "Scripture Passage" ? (
                <SaveToStudyButton
                  item={{ type: "passage", reference: verseReference }}
                  compact
                  showTab={false}
                />
              ) : null}

              <Button
                type="button"
                variant="outline-light"
                onClick={openVerseInNewTab}
                disabled={!verseUrl}
              >
                Open in New Tab
              </Button>

              <Button
                type="button"
                variant="primary"
                onClick={closeVerseModal}
              >
                Close
              </Button>
            </div>
          </Modal.Footer>
        </Modal>
      )}
    </VerseModalContext.Provider>
  );
}

export function useVerseModal() {
  const context = useContext(VerseModalContext);

  if (!context) {
    throw new Error(
      "useVerseModal must be used inside VerseModalProvider."
    );
  }

  return context;
}